import type { TileCoords } from "./tileCoverage";

/**
 * 緯度経度を Web メルカトル（EPSG:3857）のタイル座標に変換する。
 * Leaflet の既定 CRS と同じ計算で、地理院タイルの {z}/{x}/{y} に対応する。
 */
export const latLngToTileCoords = (
  lat: number,
  lng: number,
  z: number
): TileCoords => {
  const n = 2 ** z;
  const latRad = (lat * Math.PI) / 180;
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );
  return { z, x, y };
};

/**
 * 1 枚の確認を打ち切るまでの時間（ms）。
 * 応答が返らないまま Promise が解決しないと、まとめて待っている呼び出し側が
 * いつまでも結果を出せないため、この時間を過ぎたら「判定できず」として返す。
 */
export const TILE_PROBE_TIMEOUT_MS = 10_000;

/**
 * タイル 1 枚の有無の判定結果。
 * null は「判定できなかった」（応答なしで打ち切った）で、`false`（データなし）とは区別する。
 */
export type TileProbeResult = boolean | null;

/**
 * タイル画像 1 枚を読み込んで存在するかを調べる。
 * 地理院タイルはデータがない範囲を 404 で返すため、Image の onerror で「なし」と判定できる。
 * fetch ではなく Image を使うので CORS の影響を受けず、ブラウザのキャッシュも効く。
 *
 * NOTE: Image の onerror は 404 と通信失敗・ブロックを区別できないため、
 * 地理院側の障害時には「なし」と判定され得る。応答が返らないケースだけは
 * TILE_PROBE_TIMEOUT_MS で打ち切り、null（判定できず）として呼び出し側に返す。
 */
export const probeTileAvailability = async (
  url: string
): Promise<TileProbeResult> =>
  await new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    // 0 は未設定。clearTimeout(0) は何もしないので、そのまま初期値に使える
    let timer = 0;

    const finish = (result: TileProbeResult): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
      resolve(result);
    };

    image.onload = () => {
      finish(true);
    };
    image.onerror = () => {
      finish(false);
    };
    timer = window.setTimeout(() => {
      // 読み込みを止めて、これ以上の通信・デコードを続けさせない
      image.src = "";
      finish(null);
    }, TILE_PROBE_TIMEOUT_MS);

    image.src = url;
  });
